import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../Assets/Images/Vireya_Insurance.png";
import axios from "axios";
import { Toast } from "primereact/toast";

export default function ValidationPage() {
  const navigate = useNavigate();
  const toast = useRef<Toast | null>(null);

  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(false);
  const [validationStep, setValidationStep] = useState(0);

  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  const validationSteps = ["Authenticating..."];

  useEffect(() => {
    localStorage.clear();
    validateNowcertsToken(token);
  }, []);

  const validateNowcertsToken = (token: any) => {
    setLoading(true);
    setErrorState(false);

    axios({
      method: "get",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/validatessotoken?token=${token}`,
    })
      .then((response: any) => {
        if (!response.data?.token) {
          localStorage.clear();
          setErrorState(true);
          setLoading(false);
          return;
        }

        localStorage.setItem("igtoken", response.data.token);
        localStorage.setItem("userName", response.data.userName);
        localStorage.setItem("NowcertsAgentId", response.data.nowCertsAgentId);
        localStorage.setItem("AvatarName", response.data.avatarName);
        localStorage.setItem("NCToken", response.data.ncToken);
        localStorage.setItem("NCAgencyId", response.data.nowCertsAgencyId);
        localStorage.setItem("name", response.data.firstName + " " + response.data.lastName);
        localStorage.setItem("IsAdminUser", response.data.isAdminUser);
        localStorage.setItem("agentEmail", response.data.agentEmail);
        setLoading(false);
        navigate("/dashboard");
      })
      .catch((error: any) => {
        setLoading(false);
        setErrorState(true);

        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Unknown error while validating token!",
            life: 3000,
          });
        }
      });
  };

  return (
    <div className="validation-container">
      <Toast ref={toast} />

      <div className="login-logo-top">
        <img className="login-logo-top-left" src={logo} alt="Envela Insurance Logo" />
      </div>

      <div className="validation-wrapper">
        <div className="validation-card">
          <div className="validation-header">
            <div className="validation-icon-container">
              {loading ? (
                <div className="validation-loading-icon">
                  <svg className="validation-spinner" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      fill="none"
                      stroke="#36c0c4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray="31.416"
                      strokeDashoffset="31.416"
                    >
                      <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite" />
                      <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite" />
                    </circle>
                  </svg>
                </div>
              ) : errorState ? (
                <div className="validation-error-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#ef4444" />
                    <path d="M15 9l-6 6m0-6l6 6" stroke="white" strokeWidth="2" />
                  </svg>
                </div>
              ) : (
                <div className="validation-success-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#36c0c4" />
                    <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" />
                  </svg>
                </div>
              )}
            </div>

            <h1 className={`validation-title ${!loading && errorState ? "validation-title-error" : ""}`}>
              {loading ? "Authenticating..." : errorState ? "Access Denied" : "Validation Complete!"}
            </h1>

            <p className="validation-subtitle">
              {loading
                ? "Please wait while we verify your access..."
                : errorState
                ? "You are not authorized to access this system"
                : "Welcome to your secure insurance dashboard"}
            </p>
          </div>

          <div className="validation-content">
            <div className="validation-steps">
              {validationSteps.map((step, index) => (
                <div
                  key={index}
                  className={`validation-step ${index <= validationStep ? "validation-step-active" : "validation-step-pending"} ${
                    index === validationStep && loading ? "validation-step-current" : ""
                  }`}
                >
                  <div className="validation-step-icon">
                    {index < validationStep || (!loading && !errorState) ? (
                      <svg viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="#36c0c4" />
                        <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" />
                      </svg>
                    ) : index === validationStep && loading ? (
                      <div className="validation-step-spinner">
                        <svg viewBox="0 0 24 24">
                          <circle
                            cx="12"
                            cy="12"
                            r="8"
                            fill="none"
                            stroke="#36c0c4"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeDasharray="25.133"
                            strokeDashoffset="25.133"
                          >
                            <animate attributeName="stroke-dasharray" dur="1.5s" values="0 25.133;12.566 12.566;0 25.133" repeatCount="indefinite" />
                            <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-12.566;-25.133" repeatCount="indefinite" />
                          </circle>
                        </svg>
                      </div>
                    ) : (
                      <div className="validation-step-pending-icon"></div>
                    )}
                  </div>
                  <span className="validation-step-text">{step}</span>
                </div>
              ))}
            </div>

            {/* ERROR ACTIONS */}
            {!loading && errorState && (
              <div className="validation-complete-section">
                <div className="validation-error-message">
                  <h3>Authorization Failed</h3>
                  <p>Your credentials could not be verified or you don't have permission to access this system.</p>
                </div>

                <div className="validation-error-actions">
                  <button className="validation-retry-button" onClick={() => window.location.reload()}>
                    Try Again
                  </button>
                  <button className="validation-back-button" onClick={() => navigate("/")}>
                    Back to Login
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}